import { useQuery } from '@tanstack/react-query';
import { toIsoInstant } from '@swi/contracts';
import { Link, useParams } from 'react-router-dom';
import * as api from '../api/endpoints';
import { PageHeader } from '../components/layout/PageHeader';
import { AvailabilityCalendar } from '../components/reservations/AvailabilityCalendar';
import {
  Card,
  CardHeader,
  EmptyState,
  ErrorNote,
  Pill,
  Skeleton,
  buttonClass,
} from '../components/ui/primitives';
import { coords, dateTime, money, number } from '../lib/format';

export function AirshipDetailPage() {
  const { id } = useParams<{ id: string }>();
  const airshipId = Number(id);

  const airship = useQuery({
    queryKey: ['airship', airshipId],
    queryFn: () => api.getAirship(airshipId),
    enabled: Number.isFinite(airshipId),
  });

  const maintenance = useQuery({
    queryKey: ['airship', airshipId, 'maintenance'],
    queryFn: () => api.listMaintenance(airshipId),
    enabled: Number.isFinite(airshipId),
  });

  if (airship.isLoading) {
    return <Skeleton className="h-96 w-full" />;
  }
  if (airship.isError || !airship.data) {
    return <ErrorNote title="Vzducholoď se nepodařilo načíst" />;
  }

  const data = airship.data;
  const now = new Date();
  const horizon = new Date(now.getTime() + 21 * 24 * 3600_000);

  const specs: Array<[string, string]> = [
    ['Model', data.model],
    ['Třída', data.airshipClass],
    ['Kapacita', `${String(data.capacityPax)} pasažérů`],
    ['Cestovní rychlost', `${String(data.cruiseSpeedKts)} kts`],
    ['Max. vítr při startu', `${String(data.maxWindSpeedKts)} kts`],
    ['Pozemní obsluha (turnaround)', `${String(data.turnaroundMinutes)} min`],
    ['Hodinová sazba', `${money(data.hourlyRateCzk)} / h`],
    ['Domovský hub', `${data.homeHub.code} — ${data.homeHub.name}`],
  ];

  return (
    <>
      <PageHeader
        title={data.name}
        description={data.description ?? undefined}
        actions={
          <>
            <Link to="/fleet" className={buttonClass('ghost', 'sm')}>
              ← Flotila
            </Link>
            <Link
              to={`/reservations/new?airshipId=${String(data.id)}`}
              className={buttonClass('primary', 'sm')}
            >
              Rezervovat
            </Link>
          </>
        }
      />

      <div className="grid gap-4 lg:grid-cols-[1fr_360px]">
        <div className="flex flex-col gap-4">
          <Card>
            <CardHeader
              title={
                <span className="flex items-center gap-2">
                  <span className="mono text-[var(--accent)]">{data.registration}</span>
                  <Pill tone={data.status === 'ACTIVE' ? 'accent' : 'warn'}>
                    {data.status === 'ACTIVE'
                      ? 'v provozu'
                      : data.status === 'MAINTENANCE'
                        ? 'v servisu'
                        : 'vyřazena'}
                  </Pill>
                </span>
              }
              subtitle="Technické parametry"
            />
            <dl className="grid gap-2 sm:grid-cols-2">
              {specs.map(([label, value]) => (
                <div
                  key={label}
                  className="flex items-baseline justify-between gap-3 rounded-[var(--radius)] border border-[var(--border-subtle)] px-3 py-2"
                >
                  <dt className="text-[12px] text-[var(--text-muted)]">{label}</dt>
                  <dd className="mono text-[13px] font-medium">{value}</dd>
                </div>
              ))}
            </dl>
          </Card>

          <Card>
            <CardHeader
              title="Obsazenost a pozemní obsluha"
              subtitle={`Šrafovaně servis, poloprůhledně povinná pozemní obsluha ${String(data.turnaroundMinutes)} min mezi lety.`}
            />
            <AvailabilityCalendar
              airshipId={data.id}
              from={toIsoInstant(now)}
              to={toIsoInstant(horizon)}
            />
          </Card>
        </div>

        <div className="flex flex-col gap-4">
          <Card>
            <CardHeader title="Domovský hub" />
            <p className="text-sm font-medium">{data.homeHub.name}</p>
            <p className="text-[13px] text-[var(--text-secondary)]">
              {data.homeHub.city}, {data.homeHub.country}
            </p>
            <p className="mono mt-2 text-[12px] text-[var(--text-muted)]">
              {coords(data.homeHub.latitude, data.homeHub.longitude)} · {data.homeHub.timezone}
            </p>
            <p className="mt-2 text-[12px] text-[var(--text-muted)]">
              kapacita hangáru {number(data.homeHub.hangarCapacity)} vzducholodí
            </p>
          </Card>

          <Card>
            <CardHeader title="Plánovaný servis" />
            {maintenance.isLoading ? (
              <Skeleton className="h-16" />
            ) : (maintenance.data?.length ?? 0) === 0 ? (
              <EmptyState title="Žádný servis není naplánován" />
            ) : (
              <ul className="flex flex-col gap-2">
                {maintenance.data?.map((window) => (
                  <li
                    key={window.id}
                    className="rounded-[var(--radius)] border border-[var(--border-subtle)] p-2.5"
                    style={{ background: 'var(--warn-dim)' }}
                  >
                    <p className="text-[13px] font-medium">{window.reason}</p>
                    <p className="mono mt-0.5 text-[11px] text-[var(--text-secondary)]">
                      {dateTime(window.startAt)} → {dateTime(window.endAt)}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      </div>
    </>
  );
}
